import { 
  DISTANCE_LABELS_SOURCE,
  DISTANCE_LABELS_LAYER
} from './constants';
import { 
  calculateMidpoint, 
  formatDistance, 
  manhattanDistance,
  safeSetSourceData
} from './utils';


export const generateDistanceLabels = (path, pathType, locations) => {
  if (!path || path.length < 2) return [];
  
  let features = [];  // shorter name
  
  for (let i = 0; i < path.length - 1; i++) {
    const curr = path[i];
    const next = path[i + 1];
    
    const currLoc = locations.find(loc => loc.id === curr);
    const nextLoc = locations.find(loc => loc.id === next);
    
    // check if both locations exist and have valid coords
    if (currLoc && nextLoc && 
        typeof currLoc.latitude === 'number' && 
        typeof currLoc.longitude === 'number' &&
        typeof nextLoc.latitude === 'number' && 
        typeof nextLoc.longitude === 'number' &&
        !isNaN(currLoc.latitude) && !isNaN(currLoc.longitude) &&
        !isNaN(nextLoc.latitude) && !isNaN(nextLoc.longitude)) {
      
      // calc distance between points
      const distance = manhattanDistance(
        currLoc.latitude,
        currLoc.longitude,
        nextLoc.latitude,
        nextLoc.longitude
      );
      
      // skip tiny distances (less than 1m)
      if (distance < 0.001) continue;
      
      // get midpoint for label placement
      const midpoint = calculateMidpoint(
        [currLoc.longitude, currLoc.latitude],
        [nextLoc.longitude, nextLoc.latitude]
      );
      
      // add label feature
      features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: midpoint
        },
        properties: {
          distance: formatDistance(distance),
          pathType: pathType
        }
      });
    }
  }
  
  return features;
};

export const generateMultiVehicleDistanceLabels = (vehiclePaths, pathType, locations) => {
  if (!vehiclePaths || !Array.isArray(vehiclePaths) || vehiclePaths.length === 0) {
    return [];
  }
  
  const distanceFeatures = [];
  
  // Generate distance labels for each vehicle's path
  vehiclePaths.forEach((vehicle, vehicleIndex) => {
    if (!vehicle.path || vehicle.path.length < 2) return;
    
    for (let i = 0; i < vehicle.path.length - 1; i++) {
      const currentPoint = vehicle.path[i];
      const nextPoint = vehicle.path[i + 1];
      
      if (!currentPoint || !nextPoint) continue;
      
      // Handle different point formats
      let currentId, nextId;
      
      // If point is an object format (might have id property)
      if (typeof currentPoint === 'object') {
        currentId = currentPoint.id;
      } 
      // If point is a string format (direct ID)
      else if (typeof currentPoint === 'string') {
        currentId = currentPoint;
      }
      
      if (typeof nextPoint === 'object') {
        nextId = nextPoint.id;
      } 
      else if (typeof nextPoint === 'string') {
        nextId = nextPoint;
      }
      
      // Get complete location information
      const currentLocation = locations.find(loc => loc.id === currentId);
      const nextLocation = locations.find(loc => loc.id === nextId);
      
      if (!currentLocation || !nextLocation ||
          typeof currentLocation.latitude !== 'number' || 
          typeof currentLocation.longitude !== 'number' ||
          typeof nextLocation.latitude !== 'number' || 
          typeof nextLocation.longitude !== 'number' ||
          isNaN(currentLocation.latitude) || isNaN(currentLocation.longitude) ||
          isNaN(nextLocation.latitude) || isNaN(nextLocation.longitude)) {
        continue;
      }
      
      // Calculate distance between two points
      const distance = manhattanDistance(
        currentLocation.latitude,
        currentLocation.longitude,
        nextLocation.latitude,
        nextLocation.longitude
      );
      
      // Skip very small distances
      if (distance < 0.001) continue;
      
      // Calculate midpoint for label position
      const midpoint = calculateMidpoint(
        [currentLocation.longitude, currentLocation.latitude],
        [nextLocation.longitude, nextLocation.latitude]
      );
      
      // Create label feature
      distanceFeatures.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: midpoint
        },
        properties: {
          distance: formatDistance(distance),
          pathType: pathType,
          vehicleIndex: vehicleIndex // Add vehicle index for style differentiation
        }
      });
    }
  });
  
  return distanceFeatures;
};

// Throttle updates so we don't spam the map with redraws
let updateTimeout = null;

export const updateDistanceLabels = ({
  map,
  mapReady,
  sourcesReady,
  shouldShowDistanceLabels,
  shouldShowAutoPath,
  shouldShowCustomPath,
  autoPath,
  customPath,
  locations,
  vehiclePaths = null // Vehicle paths parameter
}) => {
  // Early exit conditions
  if (!shouldShowDistanceLabels || !mapReady || !sourcesReady || !map) {
    return;
  }
  
  // Throttle updates to prevent flickering
  if (updateTimeout) {
    clearTimeout(updateTimeout);
  }
  
  updateTimeout = setTimeout(() => {
    updateDistanceLabelsImmediate({
      map,
      mapReady,
      sourcesReady,
      shouldShowDistanceLabels,
      shouldShowAutoPath,
      shouldShowCustomPath,
      autoPath,
      customPath,
      locations,
      vehiclePaths
    });
    updateTimeout = null;
  }, 50);
};

/**
 * Immediate update function for distance labels (used internally)
 */
const updateDistanceLabelsImmediate = ({
  map,
  mapReady,
  sourcesReady,
  shouldShowDistanceLabels,
  shouldShowAutoPath,
  shouldShowCustomPath,
  autoPath,
  customPath,
  locations,
  vehiclePaths = null
}) => {
  
  try {
    // Check if distance labels layer exists
    const layerExists = map.getLayer(DISTANCE_LABELS_LAYER);
    if (!layerExists) {
      console.error(`VRP Distance labels layer not found: ${DISTANCE_LABELS_LAYER}`);
      return;
    }

    // Check if distance labels source exists
    const sourceExists = map.getSource(DISTANCE_LABELS_SOURCE);
    if (!sourceExists) {
      console.error(`VRP Distance labels source not found: ${DISTANCE_LABELS_SOURCE}`);
      return;
    }

    let autoDistanceFeatures = [];
    let customDistanceFeatures = [];
    
    // Handle auto path distance labels
    if (shouldShowAutoPath && autoPath) {
      if (autoPath.vehiclePaths && Array.isArray(autoPath.vehiclePaths) && autoPath.vehiclePaths.length > 0) {
        // Use multi-vehicle path mode
        autoDistanceFeatures = generateMultiVehicleDistanceLabels(autoPath.vehiclePaths, 'auto', locations);
      } else if (Array.isArray(autoPath) && autoPath.length > 1) {
        // Use single path mode
        autoDistanceFeatures = generateDistanceLabels(autoPath, 'auto', locations);
      }
    }
    
    // Handle custom path distance labels
    if (shouldShowCustomPath && customPath) {
      if (vehiclePaths && Array.isArray(vehiclePaths) && vehiclePaths.length > 0) {
        // Use multi-vehicle path mode
        customDistanceFeatures = generateMultiVehicleDistanceLabels(vehiclePaths, 'custom', locations);
      } else if (Array.isArray(customPath) && customPath.length > 1) {
        // Use single path mode
        customDistanceFeatures = generateDistanceLabels(customPath, 'custom', locations);
      }
    }
    
    // Merge all distance labels
    const allDistanceFeatures = [...autoDistanceFeatures, ...customDistanceFeatures];

    // Create proper GeoJSON structure for distance labels
    const geoJsonData = {
      type: 'FeatureCollection',
      features: allDistanceFeatures
    };

    const success = safeSetSourceData(map, mapReady, sourcesReady, DISTANCE_LABELS_SOURCE, geoJsonData);
    
    if (!success) {
      console.error('VRP: Failed to update distance labels source');
    }
    
    // Ensure distance labels layer is visible
    setDistanceLabelsVisibility(map, mapReady, shouldShowDistanceLabels);

  } catch (err) {
    console.error("VRP Error updating distance labels:", err);
  }
};

export const setDistanceLabelsVisibility = (map, mapReady, shouldShowDistanceLabels) => {
  if (!map || !mapReady) return;
  
  try {
    // Check current zoom level before setting visibility
    const currentZoom = map.getZoom();
    const shouldAutoHide = currentZoom <= 8; // City level threshold
    
    let finalVisibility;
    if (shouldAutoHide) {
      // At city level, force hide regardless of shouldShowDistanceLabels
      finalVisibility = 'none';
    } else {
      // At non-city level, respect shouldShowDistanceLabels parameter
      finalVisibility = shouldShowDistanceLabels ? 'visible' : 'none';
    }
    
    map.setLayoutProperty(DISTANCE_LABELS_LAYER, 'visibility', finalVisibility);
  } catch (err) {
    console.error("Error setting distance label visibility:", err);
  }
}; 